import React, { useEffect, useState } from "react"

interface Proc {
  pid: string
  cpu: number
  mem: number
  cmd: string
}

const INITIAL_PROCS: Proc[] = [
  { pid: "0421", cpu: 23.4, mem: 12.6, cmd: "api-gateway" },
  { pid: "0422", cpu: 18.7, mem: 8.4, cmd: "worker-pool" },
  { pid: "0423", cpu: 9.2, mem: 6.1, cmd: "embeddings-svc" },
  { pid: "0424", cpu: 6.8, mem: 5.2, cmd: "nginx" },
  { pid: "0425", cpu: 4.1, mem: 3.8, cmd: "postgres" },
  { pid: "0426", cpu: 3.0, mem: 2.7, cmd: "redis" },
]

interface LogTpl {
  lvl: "INFO" | "DEBUG" | "WARN"
  msg: string
}

const LOG_TEMPLATES: LogTpl[] = [
  { lvl: "INFO", msg: "GET  /v1/users          → 200  47ms" },
  { lvl: "INFO", msg: "POST /v1/embed          → 200 213ms" },
  { lvl: "DEBUG", msg: "cache hit               user:42:profile" },
  { lvl: "INFO", msg: "queue consumed event    order.created" },
  { lvl: "WARN", msg: "slow query 487ms        users:list" },
  { lvl: "INFO", msg: "auth ok                 jwt verified" },
  { lvl: "INFO", msg: "GET  /v1/health         → 200   4ms" },
  { lvl: "INFO", msg: "rag retrieved 8 docs    store:pinecone" },
  { lvl: "DEBUG", msg: "ws frame                connect ok" },
  { lvl: "INFO", msg: "deploy://prod           canary green" },
  { lvl: "INFO", msg: "vector index            p95 14ms" },
  { lvl: "INFO", msg: "GET  /v1/projects       → 200  82ms" },
  { lvl: "DEBUG", msg: "metric flushed          8.4kb" },
  { lvl: "WARN", msg: "rate limit 5/s          ip:10.0.4.21" },
  { lvl: "INFO", msg: "POST /v1/agents         → 202  31ms" },
]

const SEED_LOGS: string[] = [
  "[14:32:08] INFO   GET  /v1/users          → 200  47ms",
  "[14:32:09] INFO   POST /v1/embed          → 200 213ms",
  "[14:32:11] DEBUG  cache hit               user:42:profile",
  "[14:32:12] INFO   queue consumed event    order.created",
  "[14:32:13] INFO   GET  /v1/health         → 200   4ms",
  "[14:32:15] INFO   rag retrieved 8 docs    store:pinecone",
  "[14:32:16] INFO   POST /v1/agents         → 202  31ms",
  "[14:32:17] WARN   slow query 487ms        users:list",
]

const clamp = (n: number, lo: number, hi: number): number =>
  Math.max(lo, Math.min(hi, n))

const buildBar = (pct: number, width = 28): string => {
  const filled = Math.round((pct / 100) * width)
  return "█".repeat(filled) + "░".repeat(width - filled)
}

const fmtPct = (n: number): string =>
  n.toFixed(1).padStart(5, " ") + "%"

const ts = (d: Date): string => {
  const hh = String(d.getHours()).padStart(2, "0")
  const mm = String(d.getMinutes()).padStart(2, "0")
  const ss = String(d.getSeconds()).padStart(2, "0")
  return `${hh}:${mm}:${ss}`
}

const LVL_CLASS: Record<string, string> = {
  INFO: "term-lvl-info",
  DEBUG: "term-lvl-debug",
  WARN: "term-lvl-warn",
}

const padCpu = (n: number) => n.toFixed(1).padStart(5, " ")
const padMem = (n: number) => n.toFixed(1).padStart(5, " ")

// CSS hides .terminal-backdrop on (max-width: 700px); skip the JS work too.
const shouldRunTerminal = (): boolean => {
  if (typeof window === "undefined") return false
  if (window.matchMedia("(max-width: 700px)").matches) return false
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false
  return true
}

const ProcsPane: React.FC = React.memo(() => {
  const [procs, setProcs] = useState<Proc[]>(INITIAL_PROCS)

  useEffect(() => {
    if (!shouldRunTerminal()) return
    const id = window.setInterval(() => {
      setProcs(prev =>
        prev.map(p => ({
          ...p,
          cpu: clamp(p.cpu + (Math.random() - 0.5) * 3, 0.5, 60),
          mem: clamp(p.mem + (Math.random() - 0.5) * 1, 0.5, 30),
        }))
      )
    }, 2400)
    return () => window.clearInterval(id)
  }, [])

  return (
    <section className="term-pane term-pane-procs">
      <header>
        <span className="term-prompt">$</span> top -bn1 | head
      </header>
      <pre className="term-procs">
        <span className="term-procs-row term-procs-head">
          {"  PID    %CPU    %MEM   COMMAND"}
        </span>
        {procs.map(p => (
          <span key={p.pid} className="term-procs-row">
            {`  ${p.pid}  ${padCpu(p.cpu)}  ${padMem(p.mem)}   ${p.cmd}`}
          </span>
        ))}
      </pre>
    </section>
  )
})
ProcsPane.displayName = "ProcsPane"

const LogsPane: React.FC = React.memo(() => {
  const [logs, setLogs] = useState<string[]>(SEED_LOGS)

  useEffect(() => {
    if (!shouldRunTerminal()) return
    const id = window.setInterval(() => {
      const tpl =
        LOG_TEMPLATES[Math.floor(Math.random() * LOG_TEMPLATES.length)]
      const line = `[${ts(new Date())}] ${tpl.lvl.padEnd(5)}  ${tpl.msg}`
      setLogs(prev => {
        const next = prev.length >= 9 ? prev.slice(-8) : prev.slice()
        next.push(line)
        return next
      })
    }, 1800)
    return () => window.clearInterval(id)
  }, [])

  return (
    <section className="term-pane term-pane-logs">
      <header>
        <span className="term-prompt">$</span> tail -f /var/log/app.log
      </header>
      <ul className="term-logs">
        {logs.map((line, i) => {
          const m = line.match(/\]\s+(\w+)/)
          const lvl = m ? m[1] : ""
          return (
            <li
              key={`${i}-${line}`}
              className={`term-log-line ${LVL_CLASS[lvl] || ""}`}
            >
              {line}
            </li>
          )
        })}
      </ul>
    </section>
  )
})
LogsPane.displayName = "LogsPane"

const MetersPane: React.FC = React.memo(() => {
  const [cpu, setCpu] = useState<number>(34)
  const [mem, setMem] = useState<number>(58)
  const [net, setNet] = useState<number>(12)
  const [io, setIo] = useState<number>(26)

  useEffect(() => {
    if (!shouldRunTerminal()) return
    const id = window.setInterval(() => {
      setCpu(c => clamp(c + (Math.random() - 0.5) * 12, 12, 88))
      setMem(m => clamp(m + (Math.random() - 0.5) * 4, 30, 80))
      setNet(n => clamp(n + (Math.random() - 0.5) * 8, 3, 65))
      setIo(i => clamp(i + (Math.random() - 0.5) * 6, 8, 70))
    }, 2000)
    return () => window.clearInterval(id)
  }, [])

  return (
    <section className="term-pane term-pane-meters">
      <header>
        <span className="term-prompt">$</span> sysmon --live
      </header>
      <div className="term-meters">
        <div className="term-meter">
          <span className="meter-label">cpu</span>
          <span className="meter-bar">
            [<span className="meter-fill">{buildBar(cpu)}</span>]
          </span>
          <span className="meter-value">{fmtPct(cpu)}</span>
        </div>
        <div className="term-meter">
          <span className="meter-label">mem</span>
          <span className="meter-bar">
            [<span className="meter-fill">{buildBar(mem)}</span>]
          </span>
          <span className="meter-value">{fmtPct(mem)}</span>
        </div>
        <div className="term-meter">
          <span className="meter-label">net</span>
          <span className="meter-bar">
            [<span className="meter-fill">{buildBar(net)}</span>]
          </span>
          <span className="meter-value">
            {net.toFixed(1).padStart(5, " ")} MB/s
          </span>
        </div>
        <div className="term-meter">
          <span className="meter-label">i/o</span>
          <span className="meter-bar">
            [<span className="meter-fill">{buildBar(io)}</span>]
          </span>
          <span className="meter-value">{fmtPct(io)}</span>
        </div>
      </div>
    </section>
  )
})
MetersPane.displayName = "MetersPane"

const TerminalBackdrop: React.FC = () => {
  return (
    <div className="terminal-backdrop" aria-hidden="true">
      <div className="term-grid">
        <ProcsPane />
        <LogsPane />
        <MetersPane />
      </div>
    </div>
  )
}

export default React.memo(TerminalBackdrop)
